/**
 * Un archivio chiave→testo su IndexedDB, e il ripiego in memoria quando non c'è.
 *
 * ## Perché non `localStorage`
 *
 * Perché non ci sta. Il 30-09-2026, su `stream.opik.net`, misurato su un Chromium vero:
 *
 * ```
 * canali  3,45 M caratteri → scritti
 * film    2,48 M caratteri → QuotaExceededError, code 22
 * serie   0,90 M caratteri → scritti (nello spazio avanzato)
 * ```
 *
 * I canali si scrivono all'avvio e riempiono il tetto di ~5 milioni di caratteri per origine; poi si
 * apre «Film» e la scrittura solleva. Le serie invece passavano, perché più piccole di quel che
 * restava: la stessa app **funzionava a metà**, e la metà che funzionava dipendeva dall'ordine in cui
 * si aprivano le schermate. Il catalogo non c'entrava: era valido e i nostri lettori lo leggevano
 * senza una protesta.
 *
 * IndexedDB non ha quel tetto — è una frazione del disco, centinaia di megabyte anche su un
 * televisore — e c'è da Chrome 24, quindi su Tizen 5 (Chromium 63) e su ogni WebView congelata.
 *
 * ## Cosa fa quando IndexedDB non c'è
 *
 * Tiene tutto **in memoria** e lo dichiara con `persists === false`. Non è un ripiego silenzioso: la
 * funzione — guardare la televisione — resta intera, si perde solo il non-riscaricare. Fermare l'app
 * in navigazione privata sarebbe togliere tutto per proteggere una comodità.
 *
 * Chi vuole saperlo lo chiede: `localPersists` sul port arriva fin qui.
 */

/** Il nome del database e del deposito. Cambiarli è ricominciare da zero: non si fa a cuor leggero. */
const DB_NAME = 'player.cache';
const STORE = 'kv';
const DB_VERSION = 1;

export interface KeyValueStore {
  read(key: string): Promise<string | null>;
  write(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
  list(prefix: string): Promise<ReadonlyArray<string>>;
  /** Falso quando si sta tenendo tutto in memoria perché IndexedDB non c'è. */
  readonly persists: boolean;
}

/**
 * Avvolge una `IDBRequest` in una promessa.
 *
 * `onerror` e non solo `onsuccess`: una richiesta che fallisce senza gestore lascia la promessa
 * appesa per sempre, e una promessa appesa dentro un `await` è una schermata che non finisce mai di
 * caricare — il modo di fallire che non lascia né errore né traccia.
 */
function promised<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB: richiesta fallita'));
  });
}

/** L'archivio in memoria: vive quanto la pagina. Vedi l'intestazione per il perché non solleva. */
class MemoryStore implements KeyValueStore {
  readonly persists = false;
  private readonly entries = new Map<string, string>();

  read(key: string): Promise<string | null> {
    return Promise.resolve(this.entries.get(key) ?? null);
  }

  write(key: string, value: string): Promise<void> {
    this.entries.set(key, value);
    return Promise.resolve();
  }

  remove(key: string): Promise<void> {
    this.entries.delete(key);
    return Promise.resolve();
  }

  list(prefix: string): Promise<ReadonlyArray<string>> {
    return Promise.resolve([...this.entries.keys()].filter((key) => key.startsWith(prefix)));
  }
}

class IndexedDbStore implements KeyValueStore {
  readonly persists = true;

  /**
   * La connessione, aperta una volta sola e condivisa.
   *
   * È una promessa e non un database perché l'apertura è asincrona e arrivano più letture insieme —
   * all'avvio, canali e categorie partono dallo stesso `Promise.all`. Tenendo la *promessa*, tutte
   * aspettano la stessa apertura invece di aprirne una ciascuna.
   */
  private connection: Promise<IDBDatabase> | null = null;

  constructor(private readonly factory: IDBFactory) {}

  private open(): Promise<IDBDatabase> {
    if (this.connection !== null) return this.connection;
    this.connection = new Promise<IDBDatabase>((resolve, reject) => {
      const request = this.factory.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB: apertura fallita'));
      /*
        `onblocked` esiste perché due schede aperte sulla stessa app si bloccano a vicenda quando una
        vuole cambiare versione. Senza gestore la promessa resta appesa e la schermata carica per
        sempre; rifiutando, almeno chi sta sopra può dire cosa succede.
      */
      request.onblocked = () => reject(new Error("IndexedDB: un'altra scheda tiene aperta una versione diversa"));
    });
    // Se l'apertura fallisce, la prossima chiamata ci riprova invece di riusare la promessa rotta.
    this.connection.catch(() => {
      this.connection = null;
    });
    return this.connection;
  }

  private async transaction<T>(
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => Promise<T>,
  ): Promise<T> {
    const db = await this.open();
    const tx = db.transaction(STORE, mode);
    const result = await run(tx.objectStore(STORE));
    /*
      Si aspetta il **completamento della transazione**, non solo la richiesta.

      È qui che arriva `QuotaExceededError` quando il disco è davvero finito: la `put` riesce, la
      transazione aborta. Chi si fermasse alla richiesta crederebbe di aver scritto, e la scrittura
      persa non si vede fino al riavvio dopo.
    */
    if (mode === 'readwrite') {
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error('IndexedDB: transazione fallita'));
        tx.onabort = () => reject(tx.error ?? new Error('IndexedDB: transazione annullata'));
      });
    }
    return result;
  }

  async read(key: string): Promise<string | null> {
    const value = await this.transaction('readonly', (store) => promised(store.get(key)));
    return typeof value === 'string' ? value : null;
  }

  async write(key: string, value: string): Promise<void> {
    await this.transaction('readwrite', (store) => promised(store.put(value, key)));
  }

  async remove(key: string): Promise<void> {
    await this.transaction('readwrite', (store) => promised(store.delete(key)));
  }

  async list(prefix: string): Promise<ReadonlyArray<string>> {
    /*
      `getAllKeys` e non un cursore: porta indietro **solo le chiavi**, mentre un cursore sulle chiavi
      basterebbe ma costa un giro di eventi per voce. Con qualche migliaio di finestre di guida
      archiviate, sono migliaia di giri contro uno.
    */
    const keys = await this.transaction('readonly', (store) => promised(store.getAllKeys()));
    const out: string[] = [];
    for (const key of keys) {
      if (typeof key === 'string' && key.startsWith(prefix)) out.push(key);
    }
    return out;
  }
}

/**
 * L'archivio da usare: IndexedDB dove c'è, memoria dove no.
 *
 * `typeof indexedDB` e non `indexedDB !== undefined`: dove il nome non esiste affatto — non è
 * `undefined`, proprio non c'è — la seconda forma solleva `ReferenceError` invece di rispondere. È lo
 * stesso motivo per cui `storage-web` scrive `typeof window` e non `globalThis`, che su Tizen 5 non
 * esiste ([[project_tizen5_costrutti_vietati]]).
 */
export function createLocalStore(factory?: IDBFactory): KeyValueStore {
  const available = factory ?? (typeof indexedDB !== 'undefined' ? indexedDB : undefined);
  return available === undefined ? new MemoryStore() : new IndexedDbStore(available);
}

export { MemoryStore, IndexedDbStore };
